import { useGetCampaignZipCodesCount, PendingChangesIcon } from 'entities/campaign';
import { BudgetInput } from 'shared/ui/Input';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { Tooltip } from 'shared/ui/Tooltip';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { formatDecimal } from 'shared/lib/formatting';
import { isNumber } from 'shared/lib/type-guards';

interface MinIncomeFieldProps {
  campaignId: number;
  minIncome: number;
  pendingChanges: boolean;
  appliesAt: string | null;
  isEdit?: boolean;
  isFocused?: boolean;
}

export function MinIncomeField({
  isEdit,
  pendingChanges,
  campaignId,
  minIncome,
  isFocused,
  appliesAt,
}: MinIncomeFieldProps) {
  const campaignZipCount = useGetCampaignZipCodesCount(campaignId, minIncome);

  const getCampaignZipCountInfo = () => {
    if (campaignZipCount.isLoading) {
      return 'Updating ...';
    }

    if (
      campaignZipCount.data?.data &&
      isNumber(campaignZipCount.data.data.filtered) &&
      isNumber(campaignZipCount.data.data.total)
    ) {
      return `${formatDecimal(campaignZipCount.data.data.filtered)} out of ${formatDecimal(
        campaignZipCount.data.data.total
      )} ZIP Codes`;
    }
  };

  return (
    <div className="relative mb-2">
      <BudgetInput
        attributes={{
          maxLength: 9,
          ...getDataAutoTestAttributes(['input-min-income']).attributes,
        }}
        disabled={!isEdit}
        name="minimalIncome"
        placeholder="Min income"
        isFocused={isFocused}
        thousandSeparator
      />
      {isEdit && !pendingChanges && (
        <Tooltip
          placement="top-end"
          content="Enter the minimum income per family for the ZIP Codes you wish to include in your campaign."
        >
          <MaterialIcon className="absolute right-5 top-[18px] cursor-pointer text-base text-slate-300 hover:text-deep-blue-500">
            info
          </MaterialIcon>
        </Tooltip>
      )}
      {pendingChanges && (
        <PendingChangesIcon
          appliesAt={appliesAt}
          className="absolute right-5 top-[18px]"
          blockingMessage="Minimum income changes will be applied on"
        />
      )}
      <span className="pl-4 text-xs font-normal text-slate-600">{getCampaignZipCountInfo()}</span>
    </div>
  );
}
